/**
 * Transport abstraction between the typed client and the network, so the mock backend (VITE_USE_MOCKS=1) and
 * tests plug in without touching endpoint code.
 */

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface TransportRequest {
  method: HttpMethod;
  /** Path including the /api/v1 prefix and the query string. */
  url: string;
  headers: Record<string, string>;
  /** JSON body already serialized, or a multipart form (file uploads such as the company logo). */
  body: string | FormData | null;
  signal?: AbortSignal | undefined;
}

export interface TransportResponse {
  status: number;
  /** Parsed JSON body or null for empty responses. */
  body: unknown;
}

export type HttpTransport = (transportRequest: TransportRequest) => Promise<TransportResponse>;

/** Browser fetch transport; the session cookie is HttpOnly and sent automatically (SEC-03). */
export const fetchTransport: HttpTransport = async (transportRequest) => {
  const response = await fetch(transportRequest.url, {
    method: transportRequest.method,
    headers: transportRequest.headers,
    body: transportRequest.body,
    credentials: 'same-origin',
    signal: transportRequest.signal ?? null,
  });
  const responseText = await response.text();
  let parsedBody: unknown = null;
  if (responseText !== '') {
    try {
      parsedBody = JSON.parse(responseText) as unknown;
    } catch {
      parsedBody = { detail: 'invalid_json' };
    }
  }
  return { status: response.status, body: parsedBody };
};
